import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Equipment {
  id: number
  display_name: string
  requires_odometer: boolean
}

interface AssignmentData {
  id: number
  employee_name: string | null
  assigned_at: string
  start_odometer: number | null
  returned_at: string
  end_odometer: number | null
  notes: string
}

interface Props {
  equipment: Equipment
  assignment: AssignmentData
  errors?: Record<string, string[]>
}

export default function ReturnAssignment({
  equipment,
  assignment,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Equipment", href: "/equipment" },
    { title: equipment.display_name, href: `/equipment/${equipment.id}` },
    {
      title: "Assignment History",
      href: `/equipment/${equipment.id}/assignment_history`,
    },
    {
      title: "Return",
      href: `/equipment/${equipment.id}/return_assignment/${assignment.id}`,
    },
  ]

  const form = useForm({
    returned_at: assignment.returned_at ?? "",
    end_odometer: assignment.end_odometer ?? "",
    notes: assignment.notes ?? "",
  })

  form.transform((data) => ({ equipment_assignment: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.patch(`/equipment/${equipment.id}/return_assignment/${assignment.id}`)
  }

  const fieldErrors = errors ?? {}

  const formatDateTime = (value: string | null) => {
    if (!value) return "-"
    return new Date(value).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Return ${equipment.display_name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Return {equipment.display_name}
          </h1>
          <Link
            href={`/equipment/${equipment.id}/assignment_history`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            &larr; Assignment History
          </Link>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-6 space-y-2 border-b pb-4">
            <h2 className="text-lg font-medium">Assignment Info</h2>
            <div className="text-sm">
              <span className="font-medium">Assigned To:</span>{" "}
              {assignment.employee_name ?? "Unknown"}
            </div>
            <div className="text-sm">
              <span className="font-medium">Assigned On:</span>{" "}
              {formatDateTime(assignment.assigned_at)}
            </div>
            {assignment.start_odometer != null &&
              assignment.start_odometer > 0 && (
                <div className="text-sm">
                  <span className="font-medium">Start Odo/Hours:</span>{" "}
                  {Math.round(assignment.start_odometer).toLocaleString()}
                </div>
              )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="returned_at"
                className="mb-1 block text-sm font-medium"
              >
                Date Returned <span className="text-hpi-red">*</span>
              </label>
              <input
                type="datetime-local"
                id="returned_at"
                value={form.data.returned_at}
                onChange={(e) => form.setData("returned_at", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
              {fieldErrors.returned_at && (
                <p className="text-hpi-red mt-1 text-sm">
                  {fieldErrors.returned_at.join(", ")}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="end_odometer"
                className="mb-1 block text-sm font-medium"
              >
                End Odo/Hours{" "}
                {equipment.requires_odometer && (
                  <span className="text-hpi-red">*</span>
                )}
              </label>
              <input
                type="number"
                id="end_odometer"
                step="any"
                value={form.data.end_odometer}
                onChange={(e) => form.setData("end_odometer", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
              {fieldErrors.end_odometer && (
                <p className="text-hpi-red mt-1 text-sm">
                  {fieldErrors.end_odometer.join(", ")}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="notes" className="mb-1 block text-sm font-medium">
                Notes - maintenance/problems?
              </label>
              <textarea
                id="notes"
                rows={4}
                value={form.data.notes}
                onChange={(e) => form.setData("notes", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
              {fieldErrors.notes && (
                <p className="text-hpi-red mt-1 text-sm">
                  {fieldErrors.notes.join(", ")}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={form.processing}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-6 py-2 text-white disabled:opacity-50"
              >
                {form.processing ? "Returning..." : "Return Equipment"}
              </button>
              <Link
                href={`/equipment/${equipment.id}/assignment_history`}
                className="border-hpi-light-gray hover:bg-hpi-lightest-gray rounded border px-6 py-2"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
